import { useEffect, useRef, type RefObject } from "react";
import { swipeAxis, swipeStep } from "./swipe-step";

/**
 * Swipe a surface sideways to step back or forward — the DOM half of
 * `swipe-step.ts`, which holds the thresholds.
 *
 * The surface follows the finger once the gesture locks horizontal, so it
 * reads as being pushed rather than as a button press. A committed swipe
 * carries it off the side it was pushed to, calls `onStep`, and brings the
 * next period in from the other side; a short one springs back. The surface
 * is moved with an inline `transform` written straight to the element, not
 * through React state, so a finger's sixty moves a second re-render nothing.
 *
 * Touch only (a mouse drag on the grid moves a slot, and a desktop has the
 * arrows). The surface must carry `touch-action: pan-y` in CSS: the browser
 * then keeps vertical scrolling for itself and leaves sideways travel to the
 * page, so a horizontal gesture is never cancelled out from under it. Without
 * it the browser claims the pan and fires `pointercancel`.
 *
 * `ignoreSelector` names what a gesture may NOT start on — on the calendar,
 * the selected slot, whose drag owns the finger.
 */
export interface SwipeStepOptions {
  /** Off: no listeners at all (a desktop, or a view with nothing to step). */
  readonly enabled: boolean;
  /** Called with +1 (finger moved left) or -1 (finger moved right). */
  readonly onStep: (delta: 1 | -1) => void;
  readonly ignoreSelector?: string;
}

/** How long the surface takes to leave, arrive or spring back. */
const SLIDE_MS = 170;
/** How far the incoming period starts from rest, as a share of the width. */
const ENTER_FROM = 0.35;

export function useSwipeStep(
  ref: RefObject<HTMLElement | null>,
  { enabled, onStep, ignoreSelector }: SwipeStepOptions,
): void {
  // The caller's `onStep` is usually a fresh closure every render (it reads
  // the current view); a ref keeps the listeners bound once per element.
  const onStepRef = useRef(onStep);
  useEffect(() => {
    onStepRef.current = onStep;
  }, [onStep]);

  useEffect(() => {
    const el = ref.current;
    if (!enabled || el === null) return;

    const reduceMotion = (): boolean => {
      try {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      } catch {
        return false;
      }
    };
    const place = (x: number, animate: boolean): void => {
      el.style.transition = animate ? `transform ${SLIDE_MS}ms ease-out` : "none";
      el.style.transform = x === 0 ? "" : `translateX(${x}px)`;
    };
    // A timer, not `transitionend`: that event never fires for a transition
    // that did not run (the element already sat where it was told to go), and
    // a step that waited on it would then never happen. While a committed
    // swipe is leaving, a new gesture is ignored rather than fought.
    let settle: number | undefined;
    let leaving = false;
    const afterSlide = (done: () => void): void => {
      window.clearTimeout(settle);
      leaving = true;
      settle = window.setTimeout(() => {
        leaving = false;
        done();
      }, SLIDE_MS + 40);
    };

    let pointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startT = 0;
    let axis: "x" | "y" | null = null;
    let dx = 0;

    const stopTracking = (): void => {
      pointerId = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };

    const onMove = (e: PointerEvent): void => {
      if (e.pointerId !== pointerId) return;
      // A slot drag armed under this finger after all — it owns the gesture.
      if (document.body.classList.contains("cal-dragging")) {
        stopTracking();
        place(0, true);
        return;
      }
      const mx = e.clientX - startX;
      const my = e.clientY - startY;
      if (axis === null) {
        axis = swipeAxis(mx, my);
        if (axis === "y") {
          stopTracking(); // a scroll: the browser has it from here
          return;
        }
        if (axis === null) return;
      }
      dx = mx;
      place(dx, false);
    };

    const onUp = (e: PointerEvent): void => {
      if (e.pointerId !== pointerId) return;
      stopTracking();
      if (axis !== "x") return;
      const width = el.getBoundingClientRect().width;
      const step = swipeStep(dx, e.timeStamp - startT, width);
      if (step === 0) {
        place(0, true);
        return;
      }
      if (reduceMotion()) {
        place(0, false);
        onStepRef.current(step);
        return;
      }
      // Off the side it was pushed to, then the next period in from the other.
      place(step === 1 ? -width : width, true);
      afterSlide(() => {
        onStepRef.current(step);
        place((step === 1 ? width : -width) * ENTER_FROM, false);
        // Two frames: one for React to paint the new period, one so the
        // starting offset is committed before the transition back to rest.
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => place(0, true));
        });
      });
    };

    const onCancel = (e: PointerEvent): void => {
      if (e.pointerId !== pointerId) return;
      stopTracking();
      if (axis === "x") place(0, true);
    };

    const onDown = (e: PointerEvent): void => {
      if (e.pointerType !== "touch" || !e.isPrimary) return;
      if (pointerId !== null || leaving) return;
      if (
        ignoreSelector !== undefined &&
        e.target instanceof Element &&
        e.target.closest(ignoreSelector) !== null
      ) {
        return;
      }
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startT = e.timeStamp;
      axis = null;
      dx = 0;
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
    };

    el.addEventListener("pointerdown", onDown);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      stopTracking();
      window.clearTimeout(settle);
      el.style.transition = "";
      el.style.transform = "";
    };
  }, [ref, enabled, ignoreSelector]);
}
